'use client';

import type { ReactNode } from 'react';
import cs from 'classnames';
import { styled } from 'styled-components';
import * as Styled from '@lib/styles';
import TextLink from '@dvd/components/TextLink';
import type { CtaProps } from '@types';

type IconCardColumnProps = {
  /**
   * IconCard children; should be a multiple of 3 or 4
   */
  children: ReactNode[];
  /**
   * Optional CTA for the module
   */
  cta?: CtaProps;
  /**
   * Description paragraph for the module
   */
  description?: string;
  /**
   * Optional eyebrow text displayed above the headline
   */
  eyebrowText?: string;
  /**
   * Headline for the module
   */
  headline?: string;
};

const IconCardColumnContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing[4]};
`;

const IconCardColumnHeadingContainer = styled.div`
  display: grid;
  margin-block-end: ${({ theme }) => theme.spacing[3]};
  ${({ theme }) => theme.grid.columns.small}

  ${({ theme }) => theme.mq.minWidth.large} {
    ${({ theme }) => theme.grid.columns.large}
  }
`;

const IconCardColumnHeading = styled.div`
  grid-column: auto / span 2;
  text-align: center;

  ${({ theme }) => theme.mq.minWidth.large} {
    grid-column: 3 / span 8;
  }
`;

const IconCardColumnCards = styled.div`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing[2]};
  justify-content: center;

  ${({ theme }) => theme.grid.columns.small}

  & > * {
    align-self: flex-start;
    grid-column: span 2;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    ${({ theme }) => theme.grid.columns.medium}
    & > * {
      grid-column: span 3;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    ${({ theme }) => theme.grid.columns.large}
    &.threeUp > * {
      grid-column: span 4;
    }

    &.fourUp > * {
      grid-column: span 3;
    }
  }
`;

const IconCardColumns = ({
  children,
  cta = null,
  description,
  eyebrowText,
  headline,
}: IconCardColumnProps) => {
  const threeUp = children.length % 3 === 0;
  const fourUp = children.length % 4 === 0;
  return (
    <IconCardColumnContainer>
      {headline || description || eyebrowText ? (
        <IconCardColumnHeadingContainer>
          <IconCardColumnHeading>
            {eyebrowText ? (
              <Styled.Eyebrow>{eyebrowText}</Styled.Eyebrow>
            ) : null}
            {headline ? <h2>{headline}</h2> : null}
            {description ? <p>{description}</p> : null}
            {cta ? <TextLink cta={cta} /> : null}
          </IconCardColumnHeading>
        </IconCardColumnHeadingContainer>
      ) : null}
      <IconCardColumnCards className={cs({ threeUp, fourUp })}>
        {children}
      </IconCardColumnCards>
    </IconCardColumnContainer>
  );
};

export default IconCardColumns;
